import { APP_DATE_FORMAT } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoCourseEvent } from "@/shared/model/edo-course-event.model";
import { IEdoCourse } from "@/shared/model/edo-course.model";
import { getEntities } from "@/shared/reducers/entities/edo-course-event.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { overridePaginationStateWithQueryParams } from "@/shared/util/entity-utils";
import { ITEMS_PER_PAGE } from "@/shared/util/pagination.constants";
import {
  Card,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Pagination,
} from "@/components/ui";
import { Button } from "@/components/ui";
import { Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  IPaginationBaseState,
  TextFormat,
  Translate,
  getSortState,
} from "react-jhipster";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DeleteEventoInsegnamento } from "../../components/DeleteEventoInsegnamento";
import EditCourseEvent from "../../components/EditCourseEvent";

const CourseEvents = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const params = useParams<{ id: string }>();
  const [paginationState, setPaginationState] = useState<IPaginationBaseState>(
    overridePaginationStateWithQueryParams(
      getSortState(location, ITEMS_PER_PAGE, "id"),
      location.search
    )
  );
  const [editCourseEventDialog, setEditCourseEventDialog] = useState<
    | { open: true; entity: IEdoCourseEvent }
    | { open: false; entity: null }
  >({ open: false, entity: null });
  const [deleteCourseEventDialog, setDeleteCourseEventDialog] = useState<
    | { open: true; entity: IEdoCourseEvent }
    | { open: false; entity: null }
  >({ open: false, entity: null });
  const {
    updateSuccess,
    loading,
    totalItems,
    entities: courseEvents,
  } = useAppSelector<EntityState<IEdoCourseEvent>>(
    (state) => state.edoCourseEvent
  );
  const { entity: edoInsegnamentoEntity } = useAppSelector<
    EntityState<IEdoCourse>
  >((state) => state.edoCourse);

  const getAllEntities = () => {
    const filters = new URLSearchParams();
    filters.append("filter.course.id", "$eq:" + params.id);
    dispatch(
      getEntities({
        page: paginationState.activePage,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        filters: filters.toString(),
      })
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (location.search !== endURL) {
      navigate(`${location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    paginationState.itemsPerPage,
  ]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const page = searchParams.get("page");
    const sort = searchParams.get("sort");
    if (page && sort) {
      const sortSplit = sort.split(",");
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [location.search]);

  useEffect(() => {
    if (updateSuccess) {
      handleSyncList();
    }
  }, [updateSuccess]);

  const sort = (p: string) => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === "ASC" ? "DESC" : "ASC",
      sort: p,
    });
  };

  const handlePagination = (currentPage: number) =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleRowsPerPage = (rowsPerPage: number) =>
    setPaginationState({
      ...paginationState,
      itemsPerPage: rowsPerPage,
      activePage: 1,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  return (
    <>
      <Card className="surface grid h-full grid-rows-[min-content_auto_min-content] overflow-hidden rounded-3xl p-0">
        <div className="border-b border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30">
          <div className="flex flex-wrap items-center justify-between gap-3 p-4">
            <h2 className="font-heading text-heading-3 font-bold text-[var(--color-mockup-text)]">
              <Translate contentKey="edocendoApp.edoCourseEvent.home.title" />
            </h2>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <IconButton
                onClick={handleSyncList}
                disabled={loading}
                className="lg:hidden"
                aria-label="Aggiorna"
              >
                <RefreshCw className="h-4 w-4" />
              </IconButton>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSyncList}
                disabled={loading}
                className="hidden lg:inline-flex"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                <Translate contentKey="edocendoApp.edoCourseEvent.home.refreshListLabel" />
              </Button>
              <IconButton
                onClick={() =>
                  setEditCourseEventDialog({ open: true, entity: null })
                }
                disabled={loading}
                className="lg:hidden"
                aria-label="Aggiungi evento"
              >
                <Plus className="h-4 w-4" />
              </IconButton>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setEditCourseEventDialog({ open: true, entity: null })
                }
                disabled={loading}
                className="hidden lg:inline-flex"
              >
                <Plus className="mr-2 h-4 w-4" />
                Aggiungi evento
              </Button>
            </div>
          </div>
        </div>

        {courseEvents?.length > 0 ? (
          <List className="divide-y">
            {courseEvents.map((courseEvent) => (
              <ListItem
                key={courseEvent.id}
                className="flex items-center gap-3 py-3"
              >
                <ListItemText
                  primary={courseEvent.title}
                  secondary={
                    courseEvent.startDate && courseEvent.endDate ? (
                      <>
                        <TextFormat
                          value={courseEvent.startDate}
                          type="date"
                          format={APP_DATE_FORMAT}
                        />{" "}
                        {" - "}
                        <TextFormat
                          value={courseEvent.endDate}
                          type="date"
                          format={APP_DATE_FORMAT}
                        />
                      </>
                    ) : null
                  }
                />
                <ListItemSecondaryAction className="flex flex-row gap-2">
                  <IconButton
                    onClick={() =>
                      setEditCourseEventDialog({
                        open: true,
                        entity: courseEvent,
                      })
                    }
                    aria-label="Modifica"
                  >
                    <Pencil className="h-4 w-4" />
                  </IconButton>
                  <IconButton
                    onClick={() =>
                      setDeleteCourseEventDialog({
                        open: true,
                        entity: courseEvent,
                      })
                    }
                    aria-label="Elimina"
                  >
                    <Trash2 className="h-4 w-4" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : (
          !loading && (
            <div className="p-4 text-center">
              <p className="text-body text-[var(--color-mockup-text-mid)]">
                <Translate contentKey="edocendoApp.edoCourseEvent.home.notFound" />
              </p>
            </div>
          )
        )}

        <Pagination
          count={totalItems}
          page={paginationState.activePage - 1}
          rowsPerPage={paginationState.itemsPerPage}
          onPageChange={(page) => handlePagination(page + 1)}
          onRowsPerPageChange={handleRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} di ${count !== -1 ? count : "more than" + to}`
          }
        />
      </Card>

      <EditCourseEvent
        open={editCourseEventDialog.open}
        courseEvent={editCourseEventDialog.entity}
        course={edoInsegnamentoEntity}
        onClose={() => setEditCourseEventDialog({ open: false, entity: null })}
      />

      <DeleteEventoInsegnamento
        open={deleteCourseEventDialog.open}
        evento={deleteCourseEventDialog.entity}
        handleClose={() =>
          setDeleteCourseEventDialog({ open: false, entity: null })
        }
      />
    </>
  );
};

export default CourseEvents;
